import { z } from "zod";
/**
 * How far through Session 1 this person is — `GET /me/onboarding` (`D113`).
 *
 * ── PROGRESS IS DERIVED FROM STORED ROWS, NEVER FROM THE TRANSCRIPT ─────────
 * The server computes this by looking at the profile, the weight measurement,
 * the training config, the PAR-Q, the injuries and the equipment list — the
 * rows `D114` had the app write. Not from the chat. That is the whole point of
 * `D113`: a code-driven script cannot skip or repeat a question, and it can be
 * tested without a model.
 *
 * The agent reads this through the service binding to decide the next `ask`
 * (`D112`); the app reads it to know whether to show the onboarding at all.
 */
/**
 * The sixteen steps, in the order they are asked (`wire-contracts.md` §4,
 * blueprint §5.2).
 *
 * ── `parq` IS ONE STEP HERE AND SEVEN IN THE SCRIPT ─────────────────────────
 * The state machine walks `parq_1`…`parq_7` internally, because each is its
 * own question with its own `ask`. This list is the PROGRESS vocabulary, and
 * progress on the PAR-Q is binary: all seven answered, or not. A client that
 * saw seven entries here would have to know that they are one screen, which is
 * the script's business and not the client's.
 *
 * Order is meaningful — `next` is the first step not in `completed` — so
 * inserting a step anywhere but the end changes what a half-finished
 * onboarding asks next.
 */
export const ONBOARDING_STEPS = [
    "date_of_birth",
    "sex",
    "height",
    "weight",
    "goal",
    "secondary_goal",
    "days_per_week",
    "days_fixed",
    "fixed_weekdays",
    "session_minutes",
    "session_hard_limit",
    "parq",
    "current_pain",
    "equipment_place",
    "equipment",
    "experience",
];
export const onboardingStep = z.enum(ONBOARDING_STEPS);
export const onboardingProgress = z.object({
    /** Every step whose answer is stored. Not necessarily a prefix of
     *  `ONBOARDING_STEPS`: `fixed_weekdays` is skipped when the week is
     *  variable, and a skipped step counts as completed. */
    completed: z.array(onboardingStep),
    /**
     * `true` exactly when `next` is `null`. Both are on the wire because the
     * question a screen asks is *"is this person onboarded"* and the question
     * the agent asks is *"what do I ask now"*, and deriving either from the
     * other is a rule two clients would each write.
     */
    done: z.boolean(),
    /** The next step to ask, or `null` when the script is finished. */
    next: onboardingStep.nullable(),
});
//# sourceMappingURL=onboarding.js.map